import { useState, useEffect } from "react";
import i18n, { projectLanguageToLng } from "@/zapi-frontend/shared/i18n";
import { type Editor as TipTapEditorType } from "@tiptap/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { UndoIcon, RedoIcon, BoldIcon, ItalicIcon, UnderlineIcon } from "@/zapi-frontend/widgets/TipTapEditor/ui/_ToolbarIcons";
import { PILL_CLASS, ToolbarButton, Divider, TextStyleDropdown } from "@/zapi-frontend/widgets/TipTapEditor/ui/toolbarPrimitives";

type TCaseBriefEditorToolbarProps = {
  editor: TipTapEditorType | null;
  className?: string;
  language?: string;
};

export function CaseBriefEditorToolbar({ editor, className, language }: TCaseBriefEditorToolbarProps) {
  const t = i18n.getFixedT(projectLanguageToLng(language), "createProject");
  const [activeStates, setActiveStates] = useState({
    bold: false,
    italic: false,
    underline: false,
    canUndo: false,
    canRedo: false
  });

  useEffect(() => {
    if (!editor) return;
    const update = () =>
      setActiveStates({
        bold: editor.isActive("bold"),
        italic: editor.isActive("italic"),
        underline: editor.isActive("underline"),
        canUndo: editor.can().undo(),
        canRedo: editor.can().redo()
      });
    editor.on("selectionUpdate", update);
    editor.on("transaction", update);
    update();
    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", update);
    };
  }, [editor]);

  if (!editor) return null;

  return (
    <div onMouseDown={(e) => e.preventDefault()} className={cn(PILL_CLASS, className)}>
      <div className="flex items-center">
        <ToolbarButton
          label={t("editor.undo")}
          icon={UndoIcon}
          disabled={!activeStates.canUndo}
          onClick={() => editor.chain().focus().undo().run()}
        />
        <ToolbarButton
          label={t("editor.redo")}
          icon={RedoIcon}
          disabled={!activeStates.canRedo}
          onClick={() => editor.chain().focus().redo().run()}
        />
      </div>

      <Divider />

      <TextStyleDropdown editor={editor} />

      <Divider />

      <div className="flex items-center">
        <ToolbarButton
          label={t("editor.bold")}
          icon={BoldIcon}
          isActive={activeStates.bold}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          label={t("editor.italic")}
          icon={ItalicIcon}
          isActive={activeStates.italic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolbarButton
          label={t("editor.underline")}
          icon={UnderlineIcon}
          isActive={activeStates.underline}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
      </div>
    </div>
  );
}
